import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { APP_FONT_SIZE, APP_LINE_HEIGHT } from '../../constants/typography';
import {
  DATA_TABLE_CONTAINER_SX,
  DATA_TABLE_FOOTER_SX,
  DATA_TABLE_SAVE_BUTTON_SX,
  DATA_TABLE_TOOLBAR_FIELD_SX,
  DATA_TABLE_TOOLBAR_FILTERS_ROW_SX,
  DATA_TABLE_TOOLBAR_SX,
} from '../../constants/table.styles';

export const HSE_TABLE_MIN_WIDTH = 880;

export const hseToolbarSx = DATA_TABLE_TOOLBAR_SX;
export const hseToolbarFiltersRowSx = DATA_TABLE_TOOLBAR_FILTERS_ROW_SX;
export const hseToolbarFieldSx = DATA_TABLE_TOOLBAR_FIELD_SX;
export const hseTableFooterSx = DATA_TABLE_FOOTER_SX;
export const hseSaveButtonSx = DATA_TABLE_SAVE_BUTTON_SX;

export const hseTableContainerSx: SxProps<Theme> = {
  ...DATA_TABLE_CONTAINER_SX,
  maxHeight: { xs: 'none', lg: 'calc(100vh - 280px)' },
};

const hseOutlinedInputRootSx: SxProps<Theme> = {
  borderRadius: '10px',
  bgcolor: (t) =>
    t.palette.mode === 'light'
      ? alpha(t.palette.common.black, 0.02)
      : alpha(t.palette.common.white, 0.04),
  transition: 'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
  '& fieldset': {
    borderColor: (t) => alpha(t.palette.divider, 0.85),
  },
  '&:hover fieldset': {
    borderColor: (t) => alpha(t.palette.primary.main, 0.4),
  },
  '&.Mui-focused': {
    bgcolor: 'background.paper',
    boxShadow: (t) => `0 0 0 3px ${alpha(t.palette.primary.main, 0.12)}`,
    '& fieldset': {
      borderWidth: 1,
      borderColor: 'primary.main',
    },
  },
  '&.Mui-disabled': {
    bgcolor: 'action.disabledBackground',
  },
};

const hseInputRootSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': hseOutlinedInputRootSx,
};

export const hseTableSx: SxProps<Theme> = {
  minWidth: HSE_TABLE_MIN_WIDTH,
  tableLayout: 'fixed',
  borderCollapse: 'separate',
  borderSpacing: 0,
  '& .MuiTableCell-head': {
    fontWeight: 600,
    fontSize: '0.75rem',
    lineHeight: APP_LINE_HEIGHT.normal,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: 'text.secondary',
    bgcolor: (t) =>
      t.palette.mode === 'light'
        ? alpha(t.palette.grey[500], 0.06)
        : alpha(t.palette.common.white, 0.05),
    borderBottom: (t) => `1px solid ${t.palette.divider}`,
    py: 1.5,
    px: 2,
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableCell-body': {
    fontSize: APP_FONT_SIZE.sm,
    lineHeight: APP_LINE_HEIGHT.normal,
    py: 1.75,
    px: 2,
    verticalAlign: 'top',
    color: 'text.primary',
    borderBottom: (t) => `0.5px solid ${alpha(t.palette.divider, 0.4)}`,
  },
  '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even) .MuiTableCell-body': {
    bgcolor: (t) =>
      t.palette.mode === 'light'
        ? alpha(t.palette.text.primary, 0.025)
        : alpha(t.palette.common.white, 0.03),
  },
  '& .MuiTableBody-root .MuiTableRow-root:hover .MuiTableCell-body': {
    bgcolor: (t) =>
      t.palette.mode === 'light'
        ? alpha(t.palette.primary.main, 0.05)
        : alpha(t.palette.primary.main, 0.1),
  },
  '& .MuiTableRow-root:last-child .MuiTableCell-body': {
    borderBottom: 0,
  },
};

export const hseTermColumnSx: SxProps<Theme> = {
  width: '22%',
  minWidth: 168,
};

export const hseDescriptionColumnSx: SxProps<Theme> = {
  width: '38%',
  minWidth: 240,
};

export const hseCountColumnSx: SxProps<Theme> = {
  width: 120,
  minWidth: 120,
  maxWidth: 120,
  textAlign: 'center',
  px: 1.25,
};

export const hseNotesColumnSx: SxProps<Theme> = {
  width: '28%',
  minWidth: 220,
};

export const hseHeadTermsSx: SxProps<Theme> = {
  ...hseTermColumnSx,
};

export const hseHeadDescriptionSx: SxProps<Theme> = {
  ...hseDescriptionColumnSx,
};

export const hseHeadCountSx: SxProps<Theme> = {
  ...hseCountColumnSx,
  textAlign: 'center',
  whiteSpace: 'nowrap',
};

export const hseHeadNotesSx: SxProps<Theme> = {
  ...hseNotesColumnSx,
};

export const hseTermLabelSx: SxProps<Theme> = {
  display: 'block',
  fontWeight: 600,
  fontSize: '0.9375rem',
  lineHeight: 1.4,
  color: 'text.primary',
  pt: 0.25,
};

export const hseTermDefinitionSx: SxProps<Theme> = {
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  fontWeight: 400,
  fontSize: '0.875rem',
  lineHeight: 1.55,
  color: 'text.secondary',
  cursor: 'help',
};

export const hseCountInputSx: SxProps<Theme> = {
  ...hseInputRootSx,
  width: 80,
  maxWidth: '100%',
  mx: 'auto',
  display: 'block',
  '& .MuiOutlinedInput-root': {
    ...hseOutlinedInputRootSx,
    minHeight: 40,
  },
  '& .MuiOutlinedInput-input': {
    textAlign: 'center',
    fontSize: '0.9375rem',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    py: 0.875,
    px: 0.5,
    height: 40,
    boxSizing: 'border-box',
  },
  // Hide number spinners for a cleaner look
  '& input[type=number]': {
    MozAppearance: 'textfield',
    appearance: 'textfield',
  },
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
    {
      WebkitAppearance: 'none',
      margin: 0,
    },
};

export const hseNotesInputSx: SxProps<Theme> = {
  ...hseInputRootSx,
  '& .MuiOutlinedInput-root': {
    ...hseOutlinedInputRootSx,
    minHeight: 40,
    py: 0.25,
    alignItems: 'flex-start',
  },
  '& .MuiInputBase-inputMultiline': {
    fontSize: '0.875rem',
    lineHeight: APP_LINE_HEIGHT.normal,
    py: 0.875,
    px: 1.25,
    minHeight: '22px !important',
    boxSizing: 'border-box',
  },
};
